import { cloneDeep, isEqual } from 'lodash';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useFramework } from '@/framework';
import { getFontFamilyWithFallback, getPlainTextFontByPretext, measurePlainTextFontMetrics } from '@/utils/text-kit';
import { DEFAULT_FONT_FAMILY, LayerType, type ITableLayer } from '@/version-types';
import {
    DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
    DEFAULT_TABLE_CELL__LINE_HEIGHT,
    DEFAULT_TABLE_CELL__TEXT_ALIGN,
    DEFAULT_TABLE_CELL__TEXT_COLOR,
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
    TABLE_CELL_HORIZONTAL_PADDING,
} from '../../../default-configs';
import { EdgelessTableLayer } from '../../../edgeless-layer';
import addOrUpdateLayer from '../../../utils/add-or-update-layer';
import { getTableCellBorderInsets, getTableCellRects } from '../../../utils/geometry';
import { resolveTableCellDraftVerticalLayout } from '../../../utils/resolve-cell-text-layout';
import { getCellStyle } from '../../../utils/table';
import { getTableTextLineVerticalPosition } from '../../../utils/text-vertical-position';
import { useTableEditState } from '../state';
import type { TableLayerEdit } from '../table-edit';
import { normalizeCellEditorDraft, readCellEditorDraft } from './cell-editor-draft';
import './cell-editor.less';

let nextCellEditSessionId = 0;

const FONT_SUBSET_PREVIEW_DEBOUNCE_MS = 300;

type EditStatus = 'starting' | 'editing' | 'committing' | 'cancelling' | 'finished';

interface PreviewLayoutState {
    beforeVertical: number[];
    beforeHeight: number;
    lastVertical: number[];
    lastHeight: number;
    owned: boolean;
}

interface PreviewFontState {
    fontFamily: string;
    subsetName?: string;
}

interface CellEditorProps {
    layer: ITableLayer;
    cellId: string;
    onWidthChange?: (width: number) => void;
}

type CellEditorContainerStyle = React.CSSProperties & {
    '--table-cell-editor-background-color': string;
    '--table-cell-editor-cell-width': string;
    '--table-cell-editor-background-top': string;
    '--table-cell-editor-background-right': string;
    '--table-cell-editor-background-bottom': string;
    '--table-cell-editor-background-left': string;
};

const insertText = (text: string) => {
    document.execCommand('insertText', false, text.replace(/\t/g, '\u3000\u3000'));
};

const measureDomTextBaselineOffset = ({
    font,
    lineHeight,
    text,
}: {
    font: string;
    lineHeight: number;
    text: string;
}) => {
    const line = document.createElement('div');
    const baselineProbe = document.createElement('span');
    line.style.position = 'fixed';
    line.style.top = '0';
    line.style.left = '0';
    line.style.visibility = 'hidden';
    line.style.pointerEvents = 'none';
    line.style.margin = '0';
    line.style.padding = '0';
    line.style.border = '0';
    line.style.whiteSpace = 'nowrap';
    line.style.font = font;
    line.style.lineHeight = `${lineHeight}px`;
    baselineProbe.style.display = 'inline-block';
    baselineProbe.style.width = '0';
    baselineProbe.style.height = '0';
    baselineProbe.style.margin = '0';
    baselineProbe.style.padding = '0';
    baselineProbe.style.border = '0';
    baselineProbe.style.verticalAlign = 'baseline';
    line.append(document.createTextNode(text), baselineProbe);
    document.body.append(line);

    const lineRect = line.getBoundingClientRect();
    const probeRect = baselineProbe.getBoundingClientRect();
    line.remove();
    if (lineRect.height <= 0) return null;

    return probeRect.bottom - lineRect.top;
};

const getElementBorderBoxWidth = (element: HTMLElement, entry?: ResizeObserverEntry) => {
    const borderBoxSizes = entry?.borderBoxSize as ReadonlyArray<ResizeObserverSize> | ResizeObserverSize | undefined;
    const borderBoxSize = Array.isArray(borderBoxSizes) ? borderBoxSizes[0] : borderBoxSizes;
    if (borderBoxSize?.inlineSize && Number.isFinite(borderBoxSize.inlineSize)) return borderBoxSize.inlineSize;

    const computedWidth = Number.parseFloat(getComputedStyle(element).width);
    if (computedWidth > 0 && Number.isFinite(computedWidth)) return computedWidth;
    return element.offsetWidth > 0 ? element.offsetWidth : null;
};

const CellEditor = React.memo(({ layer, cellId, onWidthChange }: CellEditorProps) => {
    const framework = useFramework();
    const tableEditState = useTableEditState();
    const { getState, setState } = tableEditState;
    const [ready, setReady] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);
    const resourceRef = useRef<EdgelessTableLayer | null>(null);
    const statusRef = useRef<EditStatus>('starting');
    const composingRef = useRef(false);
    const previewFrameRef = useRef<number | null>(null);
    const previewLayoutRef = useRef<PreviewLayoutState | null>(null);
    const fontSubsetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const draftVersionRef = useRef(0);
    const disposedRef = useRef(false);
    const commitRef = useRef<() => boolean>(() => false);
    const cancelForHistoryRef = useRef<() => void>(() => undefined);
    const [sessionId] = useState(() => ++nextCellEditSessionId);

    const cellRect = useMemo(
        () => getTableCellRects(layer).find((rect) => rect.id === cellId) ?? null,
        [layer, cellId],
    );
    const backgroundInsets = useMemo(
        () =>
            cellRect
                ? getTableCellBorderInsets(layer, cellRect)
                : {
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                  },
        [layer, cellRect],
    );
    const cell = cellRect?.cell ?? null;
    const [initialContent] = useState(() => cell?.content ?? '');
    const initialContentRef = useRef(initialContent);
    const draftRef = useRef(initialContent);
    const style = cell ? getCellStyle(cell, layer.data) : null;
    const fontFamily = style?.fontFamily ?? DEFAULT_FONT_FAMILY().name;
    const activeFontFamilyRef = useRef(fontFamily);
    const [previewFont, setPreviewFont] = useState<PreviewFontState>(() => ({
        fontFamily,
        subsetName: layer.__runtime?.fontFamilyMap?.[fontFamily],
    }));
    const [domBaselineCompensation, setDomBaselineCompensation] = useState(0);
    const subsetFontFamily = previewFont.fontFamily === fontFamily ? previewFont.subsetName : undefined;
    const editorFontFamily = getFontFamilyWithFallback(fontFamily, subsetFontFamily);
    const editorFontSize = style?.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE;
    const editorFontBold = style?.bold;
    const editorFontItalic = style?.italic;
    const editorFont = getPlainTextFontByPretext({
        fontFamily: editorFontFamily,
        fontSize: editorFontSize,
        bold: editorFontBold,
        italic: editorFontItalic,
    });

    const ownsSession = () => getState().editingSessionId === sessionId;

    const closeEditor = () => {
        if (!ownsSession()) return;
        setState({
            hasEditingText: false,
            editingSessionId: null,
            baseCellsBeforeEditing: null,
        });
    };

    const cancelPreviewFrame = () => {
        if (previewFrameRef.current === null) return;
        cancelAnimationFrame(previewFrameRef.current);
        previewFrameRef.current = null;
    };

    const cancelFontSubsetTimer = () => {
        if (fontSubsetTimerRef.current === null) return;
        clearTimeout(fontSubsetTimerRef.current);
        fontSubsetTimerRef.current = null;
    };

    const invalidateFontSubsetPreview = () => {
        cancelFontSubsetTimer();
        draftVersionRef.current += 1;
    };

    const getLatestTableLayer = () => {
        const latestLayer = framework.service.flatLayer.getLayer(layer.id);
        return latestLayer?.layerType === LayerType.TABLE ? (latestLayer as ITableLayer) : null;
    };

    const ownsPreviewLayout = (latestLayer: ITableLayer) => {
        const preview = previewLayoutRef.current;
        return (
            !!preview?.owned &&
            preview.lastHeight === latestLayer.height &&
            isEqual(preview.lastVertical, latestLayer.data.baseCells.vertical)
        );
    };

    const restoreOwnedPreview = () => {
        const latestLayer = getLatestTableLayer();
        const preview = previewLayoutRef.current;
        if (!latestLayer || !preview || !ownsPreviewLayout(latestLayer)) return latestLayer;

        const restored = cloneDeep(latestLayer);
        restored.data.baseCells.vertical = [...preview.beforeVertical];
        restored.height = preview.beforeHeight;
        preview.owned = false;
        return addOrUpdateLayer({
            frameworkIdentity: framework.identity,
            layer: restored,
            resetMaterialIds: false,
        });
    };

    const applyDraftPreview = () => {
        previewFrameRef.current = null;
        if (statusRef.current !== 'editing' || !ownsSession()) return;

        const latestLayer = getLatestTableLayer();
        if (!latestLayer || latestLayer.lock) return;
        const latestCell = latestLayer.data.cells.flat().find((item) => item?.__renderId === cellId);
        if (!latestCell || latestCell.content !== initialContentRef.current) return;

        const preview = previewLayoutRef.current;
        if (preview && !ownsPreviewLayout(latestLayer)) {
            preview.owned = false;
            return;
        }

        const candidate = cloneDeep(latestLayer);
        resolveTableCellDraftVerticalLayout(candidate, cellId, draftRef.current);
        if (
            candidate.height === latestLayer.height &&
            isEqual(candidate.data.baseCells.vertical, latestLayer.data.baseCells.vertical)
        ) {
            return;
        }

        const updatedLayer = addOrUpdateLayer({
            frameworkIdentity: framework.identity,
            layer: candidate,
            resetMaterialIds: false,
        });
        previewLayoutRef.current = {
            beforeVertical: preview?.beforeVertical ?? [...latestLayer.data.baseCells.vertical],
            beforeHeight: preview?.beforeHeight ?? latestLayer.height,
            lastVertical: [...updatedLayer.data.baseCells.vertical],
            lastHeight: updatedLayer.height,
            owned: true,
        };
    };

    const scheduleDraftPreview = () => {
        if (previewFrameRef.current !== null) return;
        previewFrameRef.current = requestAnimationFrame(applyDraftPreview);
    };

    const requestDraftFontSubset = async (draftVersion: number) => {
        if (statusRef.current !== 'editing' || !ownsSession() || disposedRef.current) return;

        const latestLayer = getLatestTableLayer();
        if (!latestLayer || latestLayer.lock) return;
        const latestCell = latestLayer.data.cells.flat().find((item) => item?.__renderId === cellId);
        if (!latestCell) return;

        const requestedFontFamily = getCellStyle(latestCell, latestLayer.data).fontFamily ?? DEFAULT_FONT_FAMILY().name;
        const requestedDraft = draftRef.current;
        if (!requestedDraft) return;

        try {
            const subsetName = await framework.service.font.loadSubset(requestedFontFamily, requestedDraft, {
                registerTo: 'document',
            });
            if (
                !subsetName ||
                disposedRef.current ||
                draftVersion !== draftVersionRef.current ||
                statusRef.current !== 'editing' ||
                !ownsSession()
            ) {
                return;
            }

            const currentLayer = getLatestTableLayer();
            const currentCell = currentLayer?.data.cells.flat().find((item) => item?.__renderId === cellId);
            const currentFontFamily =
                currentLayer && currentCell
                    ? (getCellStyle(currentCell, currentLayer.data).fontFamily ?? DEFAULT_FONT_FAMILY().name)
                    : null;
            if (currentFontFamily !== requestedFontFamily) return;

            setPreviewFont({ fontFamily: requestedFontFamily, subsetName });
        } catch {
            // Keep the current subset and source/system font fallbacks while editing.
        }
    };

    const scheduleDraftFontSubset = (draftVersion: number) => {
        cancelFontSubsetTimer();
        if (!draftRef.current) return;

        fontSubsetTimerRef.current = setTimeout(() => {
            fontSubsetTimerRef.current = null;
            void requestDraftFontSubset(draftVersion);
        }, FONT_SUBSET_PREVIEW_DEBOUNCE_MS);
    };

    const updateDraft = (text: string) => {
        draftRef.current = normalizeCellEditorDraft(text);
        scheduleDraftPreview();
        const draftVersion = ++draftVersionRef.current;
        cancelFontSubsetTimer();
        if (!composingRef.current) scheduleDraftFontSubset(draftVersion);
    };

    const updateDraftFromEditor = (editor: HTMLElement) => updateDraft(readCellEditorDraft(editor));

    const insertDraftText = (text: string) => {
        insertText(text);
        if (editorRef.current) updateDraftFromEditor(editorRef.current);
    };

    const finishResource = () => resourceRef.current?.finishCellEditResourceSync({ sessionId }) ?? false;

    const prepareCommitResource = (candidate: ITableLayer) =>
        resourceRef.current?.prepareCellEditCommitResourceSync({ sessionId, layer: candidate }) ?? false;

    const abortResource = () => resourceRef.current?.abortCellEditResourceSync({ sessionId }) ?? false;

    const failAndClose = () => {
        abortResource();
        statusRef.current = 'finished';
        closeEditor();
        return false;
    };

    const finishWithoutLocalCommit = () => {
        const latestLayer = restoreOwnedPreview();
        if (!latestLayer) return failAndClose();
        if (!prepareCommitResource(latestLayer)) {
            statusRef.current = 'editing';
            return false;
        }
        if (!finishResource()) return failAndClose();
        statusRef.current = 'finished';
        closeEditor();
        return true;
    };

    const commit = () => {
        if (statusRef.current === 'finished') return true;
        if (statusRef.current !== 'editing' || !ownsSession()) return false;
        statusRef.current = 'committing';
        cancelPreviewFrame();
        invalidateFontSubsetPreview();

        try {
            const latestLayer = getLatestTableLayer();
            if (!latestLayer) {
                return failAndClose();
            }
            if (latestLayer.lock) {
                return failAndClose();
            }

            const latestCell = latestLayer.data.cells.flat().find((item) => item?.__renderId === cellId);
            if (!latestCell) {
                return failAndClose();
            }

            const initialContent = initialContentRef.current;
            if (latestCell.content !== initialContent || draftRef.current === initialContent) {
                return finishWithoutLocalCommit();
            }

            const before = cloneDeep(latestLayer);
            const preview = previewLayoutRef.current;
            if (preview && ownsPreviewLayout(latestLayer)) {
                before.data.baseCells.vertical = [...preview.beforeVertical];
                before.height = preview.beforeHeight;
            }

            const candidate = cloneDeep(latestLayer);
            const candidateCell = candidate.data.cells.flat().find((item) => item?.__renderId === cellId)!;
            candidateCell.content = draftRef.current;
            resolveTableCellDraftVerticalLayout(candidate, cellId, draftRef.current);

            if (!prepareCommitResource(candidate)) {
                statusRef.current = 'editing';
                return false;
            }

            const committed = addOrUpdateLayer({
                frameworkIdentity: framework.identity,
                layer: candidate,
                resetMaterialIds: true,
            });
            if (!finishResource()) {
                return failAndClose();
            }
            framework.service.history.write(before, cloneDeep(committed));
            statusRef.current = 'finished';
            closeEditor();
            return true;
        } catch (error) {
            console.error('Failed to commit table cell editing.', error);
            return failAndClose();
        }
    };

    commitRef.current = commit;

    useLayoutEffect(() => {
        const tableEditor = framework.service.registration.getInstance<TableLayerEdit>('TableLayerEdit');
        if (!tableEditor) return;

        return tableEditor.registerSession({
            id: sessionId,
            layerId: layer.id,
            exit: () => {
                if (editorRef.current) draftRef.current = readCellEditorDraft(editorRef.current);
                return commitRef.current();
            },
        });
    }, [framework.service.registration, layer.id, sessionId]);

    const cancelForHistory = () => {
        if (!['starting', 'editing'].includes(statusRef.current) || !ownsSession()) return;
        statusRef.current = 'cancelling';
        cancelPreviewFrame();
        invalidateFontSubsetPreview();
        restoreOwnedPreview();
        abortResource();
    };

    cancelForHistoryRef.current = cancelForHistory;

    useLayoutEffect(() => {
        return () => {
            commitRef.current();
        };
    }, []);

    useEffect(() => {
        disposedRef.current = false;
        return () => {
            disposedRef.current = true;
            if (fontSubsetTimerRef.current !== null) {
                clearTimeout(fontSubsetTimerRef.current);
                fontSubsetTimerRef.current = null;
            }
            draftVersionRef.current += 1;
        };
    }, []);

    useEffect(() => {
        if (activeFontFamilyRef.current === fontFamily) return;

        activeFontFamilyRef.current = fontFamily;
        invalidateFontSubsetPreview();
        setPreviewFont({
            fontFamily,
            subsetName: layer.__runtime?.fontFamilyMap?.[fontFamily],
        });
        if (!composingRef.current && statusRef.current === 'editing') {
            scheduleDraftFontSubset(draftVersionRef.current);
        }
        // Runtime mapping changes for the same family are applied by the request result.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fontFamily]);

    useEffect(() => {
        const historyListenerID = framework.service.history.onBeforeChange(({ isRedo, isUndo }) => {
            if (isRedo || isUndo) cancelForHistoryRef.current();
        }, 100);
        return () => framework.service.events.off(historyListenerID);
    }, [framework.service.history, framework.service.events]);

    useEffect(() => {
        setState({ editingSessionId: sessionId });
        if (!cell) {
            statusRef.current = 'finished';
            closeEditor();
            return;
        }

        const object = framework.service.edgelessCanvas.getByLayerId(layer.id);
        if (!(object instanceof EdgelessTableLayer)) {
            statusRef.current = 'finished';
            closeEditor();
            return;
        }

        resourceRef.current = object;
        let disposed = false;
        void object
            .beginCellEditResource({ cellId, sessionId })
            .then((didApply) => {
                if (disposed || !ownsSession()) return;
                if (!didApply) {
                    statusRef.current = 'finished';
                    closeEditor();
                    return;
                }
                statusRef.current = 'editing';
                setReady(true);
            })
            .catch((error) => {
                if (disposed || !ownsSession()) return;
                console.error('Failed to enter table cell editing.', error);
                object.abortCellEditResourceSync({ sessionId });
                statusRef.current = 'finished';
                closeEditor();
            });

        return () => {
            disposed = true;
            invalidateFontSubsetPreview();
            if (statusRef.current !== 'finished') {
                object.abortCellEditResourceSync({ sessionId });
            }
        };
        // Content/style updates are concurrent edits and must not restart the edit session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellId, layer.id, sessionId]);

    useEffect(() => {
        if (!ready || !editorRef.current) return;
        editorRef.current.focus();
        const selection = window.getSelection();
        if (!selection) return;
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        selection.removeAllRanges();
        selection.addRange(range);
    }, [ready]);

    useLayoutEffect(() => {
        if (!ready) return;

        const measurementText = initialContent || '\u56fd';
        const lineHeight = editorFontSize * DEFAULT_TABLE_CELL__LINE_HEIGHT;
        const canvasMetrics = measurePlainTextFontMetrics(measurementText, {
            fontFamily: editorFontFamily,
            fontSize: editorFontSize,
            bold: editorFontBold,
            italic: editorFontItalic,
        });
        const domBaselineOffset = measureDomTextBaselineOffset({
            font: editorFont,
            lineHeight,
            text: measurementText,
        });
        if (!canvasMetrics || domBaselineOffset === null) {
            setDomBaselineCompensation(0);
            return;
        }

        const canvasBaselineOffset = getTableTextLineVerticalPosition(0, lineHeight, canvasMetrics).baselineY;
        const compensation = canvasBaselineOffset - domBaselineOffset;
        setDomBaselineCompensation(Math.abs(compensation) < 1 ? compensation : 0);
    }, [editorFont, editorFontBold, editorFontFamily, editorFontItalic, editorFontSize, initialContent, ready]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!ready || !container || !onWidthChange) return;

        const reportWidth = (entry?: ResizeObserverEntry) => {
            const width = getElementBorderBoxWidth(container, entry);
            if (width !== null) onWidthChange(width);
        };
        reportWidth();
        if (typeof ResizeObserver === 'undefined') return;

        const resizeObserver = new ResizeObserver((entries) => {
            reportWidth(entries[0]);
        });
        resizeObserver.observe(container, { box: 'border-box' });
        return () => resizeObserver.disconnect();
    }, [onWidthChange, ready]);

    const cancel = async () => {
        if (!['starting', 'editing'].includes(statusRef.current) || !ownsSession()) return;
        statusRef.current = 'cancelling';
        cancelPreviewFrame();
        invalidateFontSubsetPreview();
        restoreOwnedPreview();
        try {
            const resource = resourceRef.current;
            if (resource) await resource.cancelCellEditResource({ sessionId });
        } catch (error) {
            console.error('Failed to restore the table resource after cell editing.', error);
            abortResource();
        } finally {
            statusRef.current = 'finished';
            closeEditor();
        }
    };

    if (!ready || !cellRect || !cell || !style) return null;

    const disableAutoWrap = style.disableAutoWrap ?? false;
    const editorContainerStyle: CellEditorContainerStyle = {
        top: cellRect.top,
        left: cellRect.left,
        width: disableAutoWrap ? 'max-content' : cellRect.width,
        height: cellRect.height,
        minWidth: cellRect.width,
        alignItems: {
            top: 'flex-start',
            center: 'center',
            bottom: 'flex-end',
        }[style.verticalAlign ?? DEFAULT_TABLE_CELL__VERTICAL_ALIGN],
        opacity: layer.opacity,
        '--table-cell-editor-background-color': style.backgroundColor ?? DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
        '--table-cell-editor-cell-width': `${cellRect.width}px`,
        '--table-cell-editor-background-top': `${backgroundInsets.top}px`,
        '--table-cell-editor-background-right': `${backgroundInsets.right}px`,
        '--table-cell-editor-background-bottom': `${backgroundInsets.bottom}px`,
        '--table-cell-editor-background-left': `${backgroundInsets.left}px`,
    };
    const editorContentStyle: React.CSSProperties = {
        width: disableAutoWrap ? 'max-content' : '100%',
        minWidth: disableAutoWrap ? Math.max(0, cellRect.width - TABLE_CELL_HORIZONTAL_PADDING * 2) : 0,
        fontFamily: editorFontFamily,
        fontSize: editorFontSize,
        lineHeight: DEFAULT_TABLE_CELL__LINE_HEIGHT,
        color: style.color ?? DEFAULT_TABLE_CELL__TEXT_COLOR,
        fontWeight: style.bold ? 'bold' : 'normal',
        fontStyle: style.italic ? 'italic' : 'normal',
        textAlign: style.textAlign === 'justify' ? 'left' : (style.textAlign ?? DEFAULT_TABLE_CELL__TEXT_ALIGN),
        textDecoration: `${style.underline ? 'underline' : ''} ${style.lineThrough ? 'line-through' : ''}`,
        transform: domBaselineCompensation ? `translateY(${domBaselineCompensation}px)` : undefined,
    };

    return (
        <div
            ref={containerRef}
            className={`tableLayerCellEditor${disableAutoWrap ? ' tableLayerCellEditor--no-wrap' : ''}`}
            style={editorContainerStyle}
            onMouseDown={(event) => {
                event.stopPropagation();
                if (event.target !== event.currentTarget) return;
                event.preventDefault();
                editorRef.current?.focus();
            }}
        >
            <div
                ref={editorRef}
                className="tableLayerCellEditorContent"
                style={editorContentStyle}
                contentEditable
                suppressContentEditableWarning
                spellCheck={false}
                onInput={(event) => updateDraftFromEditor(event.currentTarget)}
                onBlur={(event) => {
                    draftRef.current = readCellEditorDraft(event.currentTarget);
                    commitRef.current();
                }}
                onCompositionStart={() => {
                    composingRef.current = true;
                    invalidateFontSubsetPreview();
                }}
                onCompositionEnd={(event) => {
                    composingRef.current = false;
                    updateDraftFromEditor(event.currentTarget);
                }}
                onCopy={(event) => event.stopPropagation()}
                onCut={(event) => event.stopPropagation()}
                onPaste={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    insertDraftText(event.clipboardData.getData('text/plain'));
                }}
                onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        void cancel();
                        return;
                    }
                    if (event.key === 'Tab') {
                        event.preventDefault();
                        insertDraftText('\u3000\u3000');
                        return;
                    }
                    if (event.key === 'Enter' && !composingRef.current) {
                        event.preventDefault();
                        insertDraftText('\n');
                    }
                }}
            >
                {initialContent}
            </div>
        </div>
    );
});

CellEditor.displayName = 'TableLayerCellEditor';

export default CellEditor;
